import React, { Suspense, lazy, useState, useEffect, useRef } from 'react';
import { createBrowserRouter, RouterProvider, useLocation, useNavigate } from 'react-router-dom';
import { ApplyPreferredTheme } from '@crisp/ui-toolkit';
import { PortalTargetProvider, Spinner, Toaster } from '@crisp/crisp-ui/cabbage';
import { CrispAuthProvider, useCrispAuth } from './lib/auth0';
import { initAnalytics } from './lib/analytics';
import { fetchNavigation, fetchPage, fetchRedirects, getStoredAuthToken, PageApiError } from './lib/api';
import { Layout } from './components/layout/Layout';
import { EmbedApp } from './embed/EmbedApp';
import { NavOutput, PageOutput, RedirectItem } from './types';
import { resolveSite, slugFromSitePath } from './lib/sites';
import styles from './App.module.css';

// Authoring / preview stay lazy for TipTap and Dock chrome, not for Cabbage CSS
// (that sheet is already on the reader).
const AuthoringRoute = lazy(() => import('./authoring/AuthoringRoute'));
const BranchPreviewRoute = lazy(() => import('./preview/BranchPreviewRoute'));

export function DocsViewer() {
  const location = useLocation();
  const navigate = useNavigate();
  const site = resolveSite(location.pathname);
  const { isLoading: authLoading, isAuthenticated, getAccessToken } = useCrispAuth();
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  const [navData, setNavData] = useState<NavOutput | null>(null);
  const [pageData, setPageData] = useState<PageOutput | null>(null);
  const [loadedSlug, setLoadedSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [redirects, setRedirects] = useState<RedirectItem[]>([]);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [authSettled, setAuthSettled] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    let cancelled = false;
    (async () => {
      let token: string | null = null;
      if (isAuthenticated) {
        try {
          token = await getAccessTokenRef.current();
        } catch {
          token = null;
        }
      }
      if (!token) {
        token = getStoredAuthToken();
      }
      if (!cancelled) {
        setAccessToken(token);
        setAuthSettled(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated]);

  // 1. Load initial navigation and redirects after auth settles
  useEffect(() => {
    if (!authSettled) return;

    initAnalytics();

    fetchNavigation(site.id, accessToken)
      .then(setNavData)
      .catch((err) => console.error('Error loading navigation:', err));

    fetchRedirects()
      .then(setRedirects)
      .catch((err) => console.error('Error loading redirects:', err));
  }, [authSettled, accessToken, site.id]);

  // 2. Load page on location change & check redirects
  const relativePath = site.path ? location.pathname.slice(site.path.length) : location.pathname;
  const rawPath = relativePath.replace(/\/+$/, '') || '/';
  const slug = slugFromSitePath(location.pathname, site);
  const isCurrentPage = loadedSlug === slug;
  const showLoading = !authSettled || !isCurrentPage;
  const showError = isCurrentPage ? error : null;

  useEffect(() => {
    if (!authSettled) return;

    let cancelled = false;

    // Check if current path matches any redirect
    if (redirects.length > 0) {
      const match = redirects.find(
        (r) => r.source === rawPath || r.source === location.pathname
      );
      if (match) {
        navigate(`${site.path}${match.destination}` || '/', { replace: true });
        return () => {
          cancelled = true;
        };
      }
    }

    setError(null);

    fetchPage(slug, site.id, accessToken)
      .then((data) => {
        if (cancelled) return;
        setPageData(data);
        setLoadedSlug(slug);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(`Failed to load page ${slug}:`, err);
        if (err instanceof PageApiError && (err.status === 401 || err.status === 403)) {
          setPageData({
            slug,
            title: slug.split('/').pop()?.replace(/[-_]/g, ' ') || 'Restricted page',
            frontmatter: {},
            access: err.status === 401 ? ['authenticated'] : ['restricted'],
            hast: { type: 'root', children: [] },
            headings: [],
          });
          setError(null);
          setLoadedSlug(slug);
          return;
        }
        setError(err.message || 'Page not found');
        setLoadedSlug(slug);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, rawPath, redirects, navigate, location.pathname, authSettled, accessToken, site.id, site.path]);

  return (
    <Layout
      navData={navData}
      pageData={showLoading ? null : pageData}
      isLoading={showLoading}
      error={showError}
      routePrefix={site.path}
      site={site}
    />
  );
}

function LazyRoute({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className={styles.fallback}>
          <Spinner />
          <p>{label}</p>
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

const router = createBrowserRouter([
  { path: '/embed', element: <EmbedApp /> },
  {
    path: '/admin/author',
    element: (
      <LazyRoute label="Loading authoring workspace…">
        <AuthoringRoute />
      </LazyRoute>
    ),
  },
  {
    path: '/preview/:branch/*',
    element: (
      <LazyRoute label="Loading branch preview…">
        <BranchPreviewRoute />
      </LazyRoute>
    ),
  },
  { path: '*', element: <DocsViewer /> },
]);

export function App() {
  return (
    <CrispAuthProvider>
      <ApplyPreferredTheme default="dark" />
      <PortalTargetProvider>
        <RouterProvider router={router} />
        <Toaster />
      </PortalTargetProvider>
    </CrispAuthProvider>
  );
}

export default App;
